import React, { useEffect, useState } from "react";
import { Send, Copy, Trash2, ExternalLink, CheckCircle, Loader2 } from "lucide-react";
import Button from "../components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import SeoHead from "../components/SeoHead";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const PLATFORMS = [
  { id: "discord",   label: "Discord",   color: "#5865F2", auto: true  },
  { id: "x",         label: "X",         color: "#111827", auto: false },
  { id: "facebook",  label: "Facebook",  color: "#1877F2", auto: false },
  { id: "instagram", label: "Instagram", color: "#E1306C", auto: false },
  { id: "tiktok",    label: "TikTok",    color: "#000000", auto: false },
  { id: "kofi",      label: "Ko-Fi",     color: "#FF5F5F", auto: false },
];

const emptyDraft = (p) => ({ platform: p, title: "", text: "", image_url: "", link_url: "", hashtags: "" });

export default function AdminSocials() {
  const [active, setActive] = useState("discord");
  const [drafts, setDrafts] = useState([]);
  const [form, setForm] = useState(emptyDraft("discord"));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);

  const authed = (url, opts = {}) =>
    fetch(`${BACKEND}${url}`, { credentials: "include", ...opts });

  const loadDrafts = async () => {
    try {
      const r = await authed("/api/admin/social/drafts");
      const d = await r.json();
      setDrafts(d.drafts || []);
    } catch {}
  };
  useEffect(() => { loadDrafts(); }, []);

  const autoCompose = async () => {
    if (!window.confirm("Generare 6 bozze (una per social) dall'ultimo aggiornamento del sito?")) return;
    setAutoBusy(true); setMsg(null);
    try {
      const r = await authed("/api/admin/social/autocompose", { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "Errore generazione");
      setMsg({ kind: "ok", text: `Generate ${d.count} bozze — sorgente: ${d.source?.subject || "changelog"}` });
      loadDrafts();
    } catch (e) { setMsg({ kind: "err", text: e.message }); }
    finally { setAutoBusy(false); }
  };

  const switchTab = (p) => { setActive(p); setForm(emptyDraft(p)); setMsg(null); };

  const saveDraft = async () => {
    if (!form.text.trim()) { setMsg({ kind: "err", text: "Testo obbligatorio" }); return; }
    setBusy(true); setMsg(null);
    try {
      const r = await authed("/api/admin/social/drafts", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, platform: active }),
      });
      if (!r.ok) throw new Error("Errore salvataggio bozza");
      setMsg({ kind: "ok", text: "Bozza salvata — pronta per l'approvazione" });
      setForm(emptyDraft(active));
      loadDrafts();
    } catch (e) { setMsg({ kind: "err", text: e.message }); }
    finally { setBusy(false); }
  };

  const approve = async (draftId) => {
    if (!window.confirm("Confermi la pubblicazione? Su X/FB si aprirà il compositore ufficiale.")) return;
    setBusy(true); setMsg(null);
    try {
      const r = await authed("/api/admin/social/approve", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ draft_id: draftId }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || "Errore pubblicazione");
      if (data.published) {
        setMsg({ kind: "ok", text: "Pubblicato su Discord!" });
      } else if (data.handoff) {
        if (data.compose_text) navigator.clipboard?.writeText(data.compose_text).catch(() => {});
        setMsg({ kind: "ok", text: `Testo copiato negli appunti. Apertura ${active}…` });
        window.open(data.intent_url, "_blank", "noopener");
      }
      loadDrafts();
    } catch (e) { setMsg({ kind: "err", text: e.message }); }
    finally { setBusy(false); }
  };

  const deleteDraft = async (id) => {
    await authed(`/api/admin/social/drafts/${id}`, { method: "DELETE" });
    loadDrafts();
  };

  const platform = PLATFORMS.find((p) => p.id === active);
  const activeDrafts = drafts.filter((d) => d.platform === active);

  return (
    <div className="min-h-screen py-16 px-6">
      <SeoHead title="Admin — Social Hub" description="Gestione post social multi-piattaforma" path="/admin/socials" noindex />
      <div className="container mx-auto max-w-5xl space-y-6">
        <header>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-display text-4xl text-gold mb-2">Social Hub</h1>
              <p className="text-sm text-muted-foreground">
                Prepara le bozze e pubblica su ogni social con approvazione manuale. Nessuna API a pagamento.
              </p>
            </div>
            <Button
              onClick={autoCompose}
              disabled={autoBusy}
              data-testid="social-autocompose"
              className="whitespace-nowrap"
            >
              {autoBusy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Genera da ultimo aggiornamento
            </Button>
          </div>
        </header>

        <div className="flex flex-wrap gap-2" role="tablist" data-testid="social-tabs">
          {PLATFORMS.map((p) => (
            <button
              key={p.id}
              onClick={() => switchTab(p.id)}
              data-testid={`social-tab-${p.id}`}
              className={`px-4 h-10 rounded-md font-heading text-sm border transition-colors ${
                active === p.id ? "bg-gold text-black border-gold" : "bg-card text-parchment border-border hover:border-gold"
              }`}
            >
              {p.label} {p.auto ? "· AUTO" : "· APPROVAZIONE"}
            </button>
          ))}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Nuova bozza — {platform.label}</span>
              {!platform.auto && (
                <span className="text-[10px] font-heading uppercase tracking-wider text-arcane">
                  Handoff: aprirà il compositore ufficiale
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <input
              className="w-full h-10 px-3 rounded-md bg-background border border-border text-sm"
              placeholder="Titolo (opzionale)"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              data-testid="social-input-title"
            />
            <textarea
              className="w-full min-h-[120px] p-3 rounded-md bg-background border border-border text-sm"
              placeholder={`Testo del post (${platform.label})…`}
              value={form.text}
              onChange={(e) => setForm({ ...form, text: e.target.value })}
              data-testid="social-input-text"
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                className="h-10 px-3 rounded-md bg-background border border-border text-sm"
                placeholder="URL immagine (opzionale)"
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                data-testid="social-input-image"
              />
              <input
                className="h-10 px-3 rounded-md bg-background border border-border text-sm"
                placeholder="Link (es. https://mytharion.app/…)"
                value={form.link_url}
                onChange={(e) => setForm({ ...form, link_url: e.target.value })}
                data-testid="social-input-link"
              />
            </div>
            <input
              className="w-full h-10 px-3 rounded-md bg-background border border-border text-sm"
              placeholder="Hashtag (es. #GDR #VTT #Mytharion)"
              value={form.hashtags}
              onChange={(e) => setForm({ ...form, hashtags: e.target.value })}
              data-testid="social-input-hashtags"
            />
            <div className="flex justify-end">
              <Button onClick={saveDraft} disabled={busy || !form.text.trim()} data-testid="social-save-draft">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Salva bozza
              </Button>
            </div>
            {msg && (
              <p className={`text-sm ${msg.kind === "ok" ? "text-emerald-400" : "text-ruby"}`} data-testid="social-msg">
                {msg.text}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Bozze in attesa di approvazione — {platform.label}</CardTitle>
          </CardHeader>
          <CardContent>
            {activeDrafts.length === 0 ? (
              <p className="text-sm text-muted-foreground">Nessuna bozza per {platform.label}.</p>
            ) : (
              <ul className="space-y-3" data-testid="social-drafts-list">
                {activeDrafts.map((d) => (
                  <li key={d.draft_id} className="p-4 border border-border rounded-md bg-card/60">
                    {d.title && <p className="font-heading text-sm text-gold mb-1">{d.title}</p>}
                    <p className="text-sm text-parchment whitespace-pre-wrap mb-2">{d.compose_text || d.text}</p>
                    {d.image_url && <p className="text-xs text-muted-foreground mb-2">🖼 {d.image_url}</p>}
                    <div className="flex flex-wrap gap-2">
                      <Button
                        size="sm"
                        onClick={() => approve(d.draft_id)}
                        disabled={busy}
                        data-testid={`social-approve-${d.draft_id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1.5" />
                        {platform.auto ? "Approva e pubblica" : "Approva e apri compositore"}
                      </Button>
                      {d.intent_url && (
                        <a
                          href={d.intent_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-border text-xs hover:border-gold"
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Preview intent
                        </a>
                      )}
                      {d.compose_text && (
                        <button
                          onClick={() => navigator.clipboard?.writeText(d.compose_text)}
                          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-border text-xs hover:border-gold"
                        >
                          <Copy className="w-3.5 h-3.5" /> Copia testo
                        </button>
                      )}
                      <button
                        onClick={() => deleteDraft(d.draft_id)}
                        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-border text-xs text-ruby hover:border-ruby"
                        data-testid={`social-delete-${d.draft_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Cancella
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
